import React, { useEffect, useState } from 'react';
import { getStaffPerformanceMetrics, PerformanceMetrics } from '../services/performanceService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer } from 'recharts';
import { Award, TrendingUp, Heart, ArrowUpRight } from 'lucide-react';
import { SubscriptionGate } from './SubscriptionGate';
import { useDashboardFilters } from '../contexts/DashboardFiltersContext';
import { useStoreLocation } from '../contexts/LocationContext';
import { formatCurrency } from '../lib/formatters';

interface Props {
  tenantId: string;
}

export const StaffPerformanceDashboard: React.FC<Props> = ({ tenantId }) => {
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const { dateRange, tipType } = useDashboardFilters();
  const { activeLocationId } = useStoreLocation();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getStaffPerformanceMetrics(tenantId, dateRange, tipType, activeLocationId);
        setMetrics(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [tenantId, dateRange, tipType, activeLocationId]);

  if (loading) return (
    <div className="h-64 bg-gray-50 rounded-3xl animate-pulse flex items-center justify-center">
      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Calculating earning and retention metrics...</p>
    </div>
  );

  if (!metrics) return null;

  return (
    <SubscriptionGate featureName="Staff Performance Insights" featureId="shift-performance">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-black text-gray-900 uppercase italic tracking-tighter">Team Earning Velocity</h2>
            <p className="text-[10px] text-gray-400 font-black uppercase tracking-[0.2em] mt-1">Growth & Lifecycle Analysis</p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-2xl border border-emerald-100">
            <Heart className="text-emerald-500" size={16} />
            <span className="text-[10px] font-black text-emerald-700 uppercase tracking-widest">Retention Index: {metrics.retentionScore}%</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Earnings Trend */}
          <div className="lg:col-span-2 card p-8 group">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl">
                  <TrendingUp size={20} />
                </div>
                <div>
                  <h3 className="text-sm font-black text-gray-900 uppercase">Tip Earnings Trend</h3>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest italic">Tip accumulation velocity</p>
                </div>
              </div>
            </div>
            
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={metrics.trends}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis 
                    dataKey="date" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#94a3b8', fontSize: 9, fontWeight: 900 }}
                    dy={10}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#94a3b8', fontSize: 9, fontWeight: 900 }}
                  />
                  <ChartTooltip 
                    contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 50px rgba(0,0,0,0.1)' }}
                    itemStyle={{ fontSize: '10px', fontWeight: 900, textTransform: 'uppercase' }}
                  />
                  <Line 
                      type="monotone" 
                      dataKey="amount" 
                      stroke="#3b82f6" 
                      strokeWidth={4} 
                      dot={false}
                      activeDot={{ r: 8, strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Performers (Fat Finger Friendly List) */}
          <div className="card p-8">
            <div className="flex items-center gap-4 mb-8">
              <div className="p-3 bg-amber-50 text-amber-600 rounded-2xl">
                <Award size={20} />
              </div>
              <div>
                <h3 className="text-sm font-black text-gray-900 uppercase">Earnings Leaders</h3>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Top tip earners</p>
              </div>
            </div>

            <div className="space-y-4">
              {metrics.topPerformers.map((performer, idx) => (
                <div key={performer.staffId || performer.email || idx} className="p-4 bg-gray-50 rounded-2xl flex items-center justify-between group hover:bg-blue-50 transition-all cursor-pointer">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-white ${idx === 0 ? 'bg-amber-400 shadow-lg shadow-amber-400/20' : 'bg-gray-200 text-gray-400'}`}>
                      {idx + 1}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900 flex items-center gap-2">
                        {performer.name}
                      </p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                        {performer.posId && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[9px] font-mono font-bold uppercase tracking-wider">
                            POS ID: {performer.posId}
                          </span>
                        )}
                        {performer.staffId && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-200 text-slate-800 text-[9px] font-mono font-bold tracking-wider" title={`Full Staff ID: ${performer.staffId}`}>
                            Staff ID: {performer.staffId.slice(0, 8)}
                          </span>
                        )}
                        <span className="text-[10px] font-bold text-gray-400 uppercase">
                          Avg: {formatCurrency(performer.average)}/tx
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-black text-gray-900">{formatCurrency(performer.total)}</p>
                    <ArrowUpRight size={14} className="text-emerald-500 ml-auto mt-1" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Summary Chips */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <div className="p-4 sm:p-6 bg-white border border-gray-100 rounded-[1.5rem] sm:rounded-[2rem] shadow-sm min-w-0">
            <p className="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 truncate">Active Staff</p>
            <p className="text-lg sm:text-2xl font-black text-gray-900 truncate">{metrics.totalStaff}</p>
          </div>
          <div className="p-4 sm:p-6 bg-white border border-gray-100 rounded-[1.5rem] sm:rounded-[2rem] shadow-sm min-w-0">
            <p className="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 truncate">Mean Earnings</p>
            <p className="text-lg sm:text-2xl font-black text-blue-600 truncate" title={formatCurrency(metrics.averageTipsPerStaff)}>
              {formatCurrency(metrics.averageTipsPerStaff)}
            </p>
          </div>
          <div className="p-4 sm:p-6 bg-white border border-gray-100 rounded-[1.5rem] sm:rounded-[2rem] shadow-sm min-w-0">
            <p className="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 truncate">Stability</p>
            <p className="text-lg sm:text-2xl font-black text-emerald-600 truncate">High</p>
          </div>
          <div className="p-4 sm:p-6 bg-white border border-gray-100 rounded-[1.5rem] sm:rounded-[2rem] shadow-sm min-w-0">
            <p className="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 truncate">Risk Factor</p>
            <p className="text-lg sm:text-2xl font-black text-amber-600 truncate">Low</p>
          </div>
        </div>
      </div>
    </SubscriptionGate>
  );
};
